import { lazy, useEffect } from 'react';

import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import OpenReplay from '@openreplay/tracker';
import trackerAssist from '@openreplay/tracker-assist';
import { DataUtil } from '@syncfusion/ej2-data';
import { Routes, Route, Navigate } from 'react-router-dom';
import { forkJoin, of } from 'rxjs';
import { filter, map, mergeMap, takeUntil } from 'rxjs/operators';

import { useUnmountSubject } from '@hooks';
import { trackEvent } from 'ApplicationInsights';
import { ajaxGet } from 'common/ajax';
import { modules } from 'common/widgets';
import AdminAndSupportAdminPortal from 'components/AvaEzm/MemberEnrollment/AdminAndSupport/AdminAndSupportAdminPortal';
import { TELEHEALTH_MODULES } from 'components/Common/VirtualVisits/MyPanelHelpers';
import BenefitConfigurator from 'components/CRM/BenefitConfigurator/BenefitConfigurator';
import Layout from 'components/Layout';
import IpaAdministrationPage from 'components/ProviderData/IpaAdministration/pages/IpaAdministrationPage';

import LazyRender from './hoc/LazyRender';
import { currentUserService } from './subjects/common/CurrentUserService';
import 'css/styles.css';
import 'css/tabBadges.css';

const CohereSsoHandler = lazy(() => import('components/Authorizations/CohereSsoHandler'));
const IPADashboard = lazy(() => import('components/Dashboards/IPADashboard'));
const IPAStarDashboard = lazy(() => import('components/Dashboards/IPAStarDashboard'));
const PCPDashboard = lazy(() => import('components/Dashboards/PCPDashboard'));
const NCIPADashboard = lazy(() => import('components/Dashboards/NCIPADashboard'));
const StarDashboard = lazy(() => import('components/Dashboards/StarDashboard'));
const FMCDashboard = lazy(() => import('components/Dashboards/FMCDashboard'));
const AdtErDashboard = lazy(() => import('components/Dashboards/AdtErDashboard'));

const AuthorizationGuideLines = lazy(() => import('components/Common/Guidelines/IndexGuideLines'));
const ClaimsGuideLines = lazy(() => import('components/Common/Guidelines/IndexGuideLines'));
const EligibilityuideLines = lazy(() => import('components/Common/Guidelines/IndexGuideLines'));
const ReportsSubmissionGuideLines = lazy(() => import('components/Common/Guidelines/IndexGuideLines'));

const PatientPriorityWorkList = lazy(() => import('components/PatientPriority/WorkList'));
const PatientPriorityAllPatients = lazy(() => import('components/PatientPriority/AllPatients'));
const PatientPriorityDashboards = lazy(() => import('components/PatientPriority/Dashboards'));
const PatientPriorityGuidelines = lazy(() => import('components/PatientPriority/PatientPriorityGuidelines'));
const PatientPriorityGapsReview = lazy(() => import('components/PatientPriority/GapsReview'));
const PatientPrioritySupplementalReview = lazy(() => import('components/PatientPriority/SupplementalReview'));
const PatientPriorityAudit = lazy(() => import('components/PatientPriority/Audit/AuditPanel'));

const ChartsSearch = lazy(() => import('components/AvaCharts/Search'));
const ChartsUpload = lazy(() => import('components/AvaCharts/Upload'));
const ChartsSplitAndValidation = lazy(() => import('components/AvaCharts/SplitAndValidation'));
const ChartsStarsCoderView = lazy(() => import('components/AvaCharts/StarsCoderView'));
const ChartsMraCoderView = lazy(() => import('components/AvaCharts/MraCoderView'));
const ChartsStarsAdmin = lazy(() => import('components/AvaCharts/StarsAdmin'));
const ChartsDashboard = lazy(() => import('components/AvaCharts/Dashboard'));

const UserManagementUsers = lazy(() => import('components/UserManagement/Users/Users'));
const UserManagementOrganizations = lazy(() => import('components/UserManagement/Organizations/Organizations'));
const UserManagementDomains = lazy(() => import('components/UserManagement/Domains/DomainsGrid'));
const UserManagementUserReport = lazy(() => import('components/UserManagement/UserReport'));
const UserManagementGroupTab = lazy(() => import('components/UserManagement/GroupTab'));
const UserManagementProviders = lazy(() => import('components/UserManagement/Providers'));
const UserManagementUserEvents = lazy(() => import('components/UserManagement/UserEvents'));
const AvaUsersImporter = lazy(() => import('components/UmTools/AvaUsersImporter/AvaUsersImporterPage'));

const LocalUserManagementUserTab = lazy(() => import('./components/LocalUserManagement/LocalUserTab'));
const LocalUserManagementFaqs = lazy(() => import('./components/LocalUserManagement/Faqs'));

const Encounters = lazy(() => import('components/Encounters/Search/Encounters'));
const PendingClaims = lazy(() => import('components/ClaimsAuditor/PendingClaims'));
const ClaimsAuditorAdmin = lazy(() => import('components/ClaimsAuditor/Admin'));
const ClaimsAuditorRoles = lazy(() => import('components/ClaimsAuditor/RoleManagement'));
const AdvancedAudits = lazy(() => import('components/ClaimsAuditor/AdvancedAudits'));

const SNPTeamMeber = lazy(() => import('components/CareManagement/SNPMember/SNPMember'));
const PostDischargeProgramManager = lazy(
    () => import('components/CareManagement/PostDischargeProgramManager/PostDischargeProgramManagerPanel'),
);
const OutpatientTeam = lazy(() => import('components/CareManagement/OutpatientTeam/OutpatientTeam'));
const CMPaperHRA = lazy(() => import('components/CareManagement/CMPaperHRA/CMPaperHRA'));
const CMDashboards = lazy(() => import('components/CareManagement/dashboards/Dashboards'));

const EligibilityAndBenefits = lazy(() => import('components/Eligibility/EligibilityAndBenefits'));
const EligibilityAndBenefitsAgent = lazy(() => import('components/Eligibility/EligibilityAndBenefitsAgent'));
const EligibilityAndBenefitsSpecialist = lazy(() => import('components/Eligibility/EligibilityAndBenefitsSpecialist'));

const Claims = lazy(() => import('components/Claims/Claims'));
const ClaimsInternal = lazy(() => import('components/Claims/ClaimsInternal'));
const Authorization = lazy(() => import('components/Authorizations/Authorizations'));
const UMFaxManagement = lazy(() => import('components/UM/UMFaxManagement'));
const UMAuthorizationWrapper = lazy(() => import('./components/Common/UMAuthorizations/UMAuthorizationWrapper'));
const UMProductivityParent = lazy(() => import('components/UM/Productivity/UMProductivityParent'));
const UMAdmin = lazy(() => import('components/UM/UMAdmin'));
const ADTAdmissionsWrapper = lazy(() => import('components/UM/ADTAdmissionsWrapper'));
const AVADailyAdmit = lazy(() => import('components/CensusDetails/AVADailyAdmission'));
const AVADailyCensus = lazy(() => import('components/CensusDetails/AVADailyCensus'));
const AVADailyDischarge = lazy(() => import('components/CensusDetails/AVADailyDischarge'));
const AuthMetrics = lazy(() => import('components/UM/AuthMetrics'));

const Home = lazy(() => import('components/Layout/Home'));
const MessagesMainTab = lazy(() => import('components/Messaging/MessagesMainTab'));
const MemberMessages = lazy(() => import('components/Messaging/MemberMessages/Interactions'));
const Patient360 = lazy(() => import('components/Patient360/Patient360'));
const CawStatusChangeModule = lazy(() => import('components/Patient360/CawStatusChange/CawStatusChangeModule'));
const ClinicalJourney = lazy(() => import('components/Patient360/ClinicalJourney/ClinicalJourneyTab'));
const Outreach = lazy(() => import('components/JSA/Outreach/Outreach'));
const JSAAdmin = lazy(() => import('components/JSA/Admin/Admin'));
const Assessment = lazy(() => import('components/JSA/AssessmentPanel/AssessmentPanel'));
const AuditHistory = lazy(() => import('components/AuditHistory/AuditHistoryPage'));
const ProviderSearch = lazy(() => import('components/ProviderSearchModule/ProviderSearch'));
const DailyCensus = lazy(() => import('components/Census/DailyCensus'));
const DailyAdmission = lazy(() => import('components/Census/DailyAdmission'));
const DailyDischarge = lazy(() => import('components/Census/DailyDischarge'));

const AG360 = lazy(() => import('components/AppealsAndGrievances/AppealsAndGrievance360View'));

// Provider CRM
const ProviderCRMMyQueue = lazy(() => import('components/ProviderCRM/ProviderCRMMyQueue'));

// Provider Appeals and Grievances
const ProviderAppealsAndGrievances = lazy(() => import('components/ProviderAppealsAndGrievances/ag360'));

// CRM
const MemberPortalAdminConsole = lazy(
    () => import('components/CRM/MemberPortalAdminConsole/MemberPortalAdminConsolePage'),
);
const MemberPortalBannerManager = lazy(() => import('components/CRM/MemberPortalBannerManager'));
const UpsertMemberPortalBannerManager = lazy(
    () => import('components/CRM/MemberPortalBannerManager/UpsertMemberPortalBannerManager'),
);
const MyQueue = lazy(() => import('components/CRM/MyQueue'));
const CampaignStats = lazy(() => import('components/CRM/CampaignStats/CampaignStats'));
const AgentsProductivity = lazy(() => import('components/CRM/AgentProductivity/AgentsProductivity'));
const CommunicationsManager = lazy(() => import('components/CRM/CommunicationsManager/CommunicationsManager'));
const CampaignConfigurator = lazy(() => import('components/CRM/CampaignConfigurator'));
const AdvancedSearch = lazy(() => import('components/CRM/AdvancedSearch'));
const DisenrollmentDashboards = lazy(() => import('components/CRM/CrmDashboards'));
const MemberEscalation = lazy(() => import('components/CRM/MemberEscalationForm'));
const QueueReports = lazy(() => import('components/CRM/QueueReports'));

const CaseReassign = lazy(() => import('components/CrmAdmin/CrmAutoAssign/CaseReassignHome'));
const TextReplace = lazy(() => import('components/CrmAdmin/TextReplace/TextReplace'));
const FormBuilder = lazy(() => import('components/CrmAdmin/FormBuilder/NewFormBuilder'));
const CampaignHome = lazy(() => import('components/CrmAdmin/Campaign/CampaignHome'));
const CrmServiceActions = lazy(() => import('components/CrmAdmin/ServiceFlows/ServiceFlowsHome'));
const CrmQueues = lazy(() => import('components/CrmAdmin/Queues/Queues'));
const CrmRequestPlanMaterials = lazy(() => import('components/CrmAdmin/RequestPlanMaterials/RequestPlanMaterials'));
const CrmStickyNotes = lazy(() => import('components/CrmAdmin/StickyNotes/StickyNotesHome'));
const CrmAdminBenefits = lazy(() => import('components/CrmAdmin/Benefits/BenefitsHome'));

const MyPanel = lazy(() => import('components/PanelManagement/MyPanel'));
const AdminPanel = lazy(() => import('components/PanelManagement/AdminPanel'));
const CAWStatusWorklist = lazy(() => import('components/PanelManagement/CAWStatusChange'));

const MyCoDPanel = lazy(() => import('components/Common/VirtualVisits/MyCoDPanel'));

const CareOnDemandAuditHistoryTab = lazy(() => import('components/CareOnDemand/AuditHistoryTab'));
const LiveDashboardTab = lazy(() => import('components/CareOnDemand/LiveDashboardTab/LiveDashboardTab'));
const ProviderManagerPanel = lazy(() => import('components/CareOnDemand/ProviderManager/ProviderManagerPanel'));
const CODAppointmentAdmin = lazy(() => import('components/CareOnDemand/Admin/CODAdmin'));
const Coder360 = lazy(() => import('components/Coder360/Coder360'));
const Coder360SubmissionsModule = lazy(() => import('components/Coder360/Coder360SubmissionsModule'));
const Coder360MyCRRsAdminModule = lazy(() => import('components/Coder360/Coder360MyCRRsModule'));

const Dashboard = lazy(() => import('components/MyOffice/Dashboard'));

const EzmMemberAdminPage = lazy(() => import('components/AvaEzm/Member/MemberAdminPage'));

const EzmServiceOrganizationAdmin = lazy(
    () => import('components/ProviderData/ServiceOrganization/ServiceOrganizationAdminPage'),
);

const EzmProviderGroupAdmin = lazy(() => import('components/ProviderData/ProviderGroup/ProviderGroupAdminPage'));

const EzmBatchJobStatusReport = lazy(() => import('components/AvaEzm/CmsImport/BatchJobStatus/BatchImportStatusPage'));

const EzmBatchJobErrorReport = lazy(() => import('components/AvaEzm/CmsImport/BatchJobError/BatchJobErrorPage'));

const EzmTokenViewer = lazy(() => import('components/AvaEzm/Utilities/TokenViewerPage'));

// The module is becomig part of CRM
const CrmLoginAccounts = lazy(() => import('components/CRM/MemberPortalAdmin/MemberPortalAdminPage'));

const CRMMemberPortalAdminAuditHistoryPage = lazy(
    () => import('components/CRM/MemberPortalAdmin/MemberAuditHistoryPage'),
);

const EzmProviderPortalAdminReport = lazy(
    () => import('components/AvaEzm/ProviderPortalAdmin/ProviderPortalAdminPage'),
);
const EzmHealthPlanManagementBenefit = lazy(
    () => import('components/AvaEzm/HealthPlanManagementBenefit/HealthPlanManagementBenefitPage'),
);
const EzmHealthPlanManagement = lazy(() => import('components/AvaEzm/HealthPlanManagement/HealthPlanManagementPage'));

const InvalidMemberEnrollment = lazy(() => import('components/InvalidMemberEnrollments/InvalidMemberEnrollmentsPage'));

const EobNotificationsHistoryViewer = lazy(
    () => import('components/EobEmailNotificationHistory/EobEmailNotificationHistoryPage'),
);

const ProviderToolsAccountEditor = lazy(
    () => import('components/ProviderTools/AccountEditor/ProviderToolsAccountEditPage'),
);

const ProviderToolsEligibility = lazy(
    () => import('components/ProviderTools/MemberEligibility/ProviderToolsMemberEligibilityPage'),
);

const ProviderToolsReportUpload = lazy(
    () => import('components/ProviderTools/ReportsSubmission/ProviderToolsReportsSubmissionPage'),
);

const ProviderToolsAdmin = lazy(() => import('components/ProviderToolsAdmin'));

const ContractInfo = lazy(() => import('components/ProviderData/ContractAdmin/ContractInfoPage'));

const HospitalMaintenance = lazy(() => import('components/ProviderData/FacilityAdmin/HospitalMaintenancePage'));
const TemplateManager = lazy(() => import('components/ProviderData/DOFRTemplate/TemplateManagerPage'));

const DataImportUtility = lazy(() => import('components/ProviderData/X_DataImportUtility/DataImportPage'));
const ProviderDataUtility = lazy(() => import('components/ProviderData/DataUtility/DataUtilityPage'));
const ProviderDataUpdateRequest = lazy(
    () => import('components/ProviderData/DirectoryChangeRequest/DataUpdateRequestPage'),
);

const ProviderCalendar = lazy(() => import('components/AvaScheduler/MyProviderSchedulerPanel'));
const AdminApplications = lazy(() => import('components/Admin'));
const ContentManager = lazy(() => import('components/ContentManager'));
const ContentLibrary = lazy(() => import('components/ContentLibrary/ContentLibraryContainer'));
const PerformanceModule = lazy(() => import('components/Admin/Performance/PerformanceModule'));

const StaticFileManager = lazy(() => import('components/Admin/StaticFileManager'));

const ApprovalWorkflowPage = lazy(() => import('components/AvaEzm/ApprovalWorkflow/ApprovalWorkflowPage'));

// Broker Portal
const NotificationManagerPage = lazy(() => import('components/SalesAgent/NotificationManager/NotificationManagerPage'));
const FileManagerBroker = lazy(() => import('components/BrokerPortal/FileManager/Overview'));
const BrokerAnalytics = lazy(() => import('components/BrokerPortal/Analytics'));

const BrokerAccountManager = lazy(() => import('components/BrokerPortal/AccountManager/index'));
const BrokerCommissions = lazy(() => import('components/BrokerPortal/Commissions'));
const BrokerReports = lazy(() => import('components/BrokerPortal/Reports'));
const BrokerUserManager = lazy(() => import('components/BrokerPortal/UserSecurity'));
const BrokerUserGuides = lazy(() => import('components/BrokerPortal/UserGuide'));
const BrokerCasesManager = lazy(() => import('components/BrokerPortal/CRM/index'));
const BrokerPortalAdmin = lazy(() => import('components/BrokerPortal/PortalAdmin/index'));

// SalesBroker and SalesRep
const BrokerSearch = lazy(() => import('components/AvaEzm/SalesBroker/Broker360'));
const SalesRepSearch = lazy(() => import('components/AvaEzm/SalesRep/SalesAgentAdminPage'));
const SalesRepAdminPage = lazy(() => import('components/SalesAgent/SalesAgent/SalesAgentPage'));
const CertificationSessionManager = lazy(
    () => import('components/SalesAgent/CertificationSessionManager/CertificationSessionManagerPage'),
);

// Compliance Report
const ComplianceReportSearch = lazy(() => import('components/AvaEzm/ComplianceReport/ComplianceAdminPage'));

//Web Enrollments
const WebEnrollmentsSearch = lazy(() => import('components/AvaEzm/WebEnrollments/WebEnrollmentsAdminPage'));

// Review Web Enrollment
const ReviewWebEnrollment = lazy(() => import('components/AvaEzm/ReviewWebEnrollment/ReviewWebEnrollmentDetails'));

const CMSEligibilityCheck = lazy(() => import('components/AvaEzm/CMSEligibilityCheck/CMSEligibilityCheck'));

// Member Enrollment
const MemberEnrollment = lazy(
    () => import('components/AvaEzm/MemberEnrollment/MemberEnrollmentDetails/MemberEnrollmentAdminPage'),
);

// Member Enrollment Submission (Automation)
const MemberEnrollmentSubmission = lazy(
    () => import('components/AvaEzm/MemberEnrollmentSubmission/MemberEnrollmentSubmissionPage'),
);

// Paper Application Uploader
const MemberEnrollmentUploadAdmin = lazy(
    () => import('components/AvaEzm/MemberEnrollmentUpload/MemberEnrollmentUploadAdmin'),
);

// Member Enrollment Reports and Utilities
const MemberEnrollmentReportAndUtilitiesAdmin = lazy(
    () => import('components/AvaEzm/MemberEnrollment/ReportsAndUtilities/ReportsAndUtilitiesAdmin'),
);

const MemberPCPAssignmentTasks = lazy(
    () => import('components/AvaEzm/MemberEnrollment/MemberPCPAssignmentTasks/MemberPCPAssignmentTasksPage'),
);

const MemberEnrollmentConfigurations = lazy(
    () => import('components/AvaEzm/MemberEnrollmentConfigurations/MemberEnrollmentConfigurationsPage'),
);

const ReturnMailEscalations = lazy(() => import('components/ReturnMailEscalations/ReturnMailEscalations'));

const PADR = lazy(() => import('components/PADR'));

const RetroPcpAssignment = lazy(() => import('components/AvaEzm/RetroPcpAssignment/Parent/RetroPCP'));

DataUtil.odv4UniOperator['any'] = 'any';
DataUtil.fnOperators['any'] = function () {
    return true;
};

const App = () => {
    const unmountSubscriptions$ = useUnmountSubject();

    useEffect(() => {
        currentUserService
            .get()
            .pipe(
                filter((user: any) => user?.email),
                mergeMap((user) =>
                    forkJoin(
                        of(user),
                        ajaxGet('api/me/config').pipe(
                            map(({ response }) => response),
                            takeUntil(unmountSubscriptions$),
                        ),
                    ),
                ),
                takeUntil(unmountSubscriptions$),
            )
            .subscribe(([user, config]) => {
                if (user.sessionRecordingEnabled && config.openReplayProjectKey && config.openReplayIngestPoint) {
                    const tracker = new OpenReplay({
                        projectKey: config.openReplayProjectKey,
                        ingestPoint: config.openReplayIngestPoint,
                        onStart: () => trackEvent('SessionRecordingStarted', SeverityLevel.Information),
                    });
                    if (user.liveAssistEnabled) {
                        tracker.use(
                            trackerAssist({
                                onAgentConnect: () => trackEvent('LiveAssistStarted', SeverityLevel.Information),
                                onCallStart: () => trackEvent('LiveAssistCallStarted', SeverityLevel.Information),
                            }),
                        );
                    }
                    tracker.start({
                        userID: user.email,
                    });
                }
            });
    }, []);

    if (document.location.pathname.startsWith('/cohere-sso')) {
        const params = new URLSearchParams(document.location.search);

        return (
            <Routes>
                <Route
                    path='/cohere-sso'
                    element={
                        <CohereSsoHandler
                            memberId={params.get('memberId')}
                            fromUM={params.get('fromUM') === 'true'}
                        />
                    }
                />
            </Routes>
        );
    }

    return (
        <LocalizationProvider dateAdapter={AdapterLuxon}>
            <Layout>
                <Routes>
                    <Route
                        path='/signin-oidc'
                        element={<Navigate to='/' />}
                    />
                    <Route
                        path='/'
                        element={LazyRender(Home, {}, null)}
                    />
                    <Route
                        path='/p360/patient360'
                        element={LazyRender(Patient360, {}, 12)}
                    />
                    <Route
                        path='/p360/cawstatuschange'
                        element={LazyRender(CawStatusChangeModule, {}, 211)}
                    />
                    <Route
                        path='/p360/clinicaljourney'
                        element={LazyRender(ClinicalJourney, {}, 77)}
                    />
                    <Route
                        path='/messages'
                        element={LazyRender(MessagesMainTab, {}, 59)}
                    />
                    <Route
                        path='/memberinquiries'
                        element={LazyRender(MemberMessages, {}, 97)}
                    />
                    <Route
                        path='/panelmgt/mypanel'
                        element={LazyRender(MyPanel, {}, 51)}
                    />
                    <Route
                        path='/panelmgt/admin'
                        element={LazyRender(AdminPanel, {}, 53)}
                    />
                    <Route
                        path='/panelmgt/cawstatusworklist'
                        element={LazyRender(CAWStatusWorklist, {}, 212)}
                    />
                    <Route
                        path='/JSA/Assessment'
                        element={LazyRender(Assessment, {}, 34)}
                    />
                    <Route
                        path='/JSA/Outreach/'
                        element={LazyRender(Outreach, {}, 33)}
                    />
                    <Route
                        path='/JSA/Admin/'
                        element={LazyRender(JSAAdmin, {}, 134)}
                    />
                    <Route
                        path='/dashboards/ipa'
                        element={LazyRender(IPADashboard, {}, 17)}
                    />
                    <Route
                        path='/dashboards/ncipa'
                        element={LazyRender(NCIPADashboard, {}, 27)}
                    />
                    <Route
                        path='/dashboards/pcp'
                        element={LazyRender(PCPDashboard, {}, 26)}
                    />
                    <Route
                        path='/dashboards/star'
                        element={LazyRender(StarDashboard, {}, 25)}
                    />
                    <Route
                        path='/dashboards/fmc'
                        element={LazyRender(FMCDashboard, {}, 196)}
                    />
                    <Route
                        path='/dashboards/adter'
                        element={LazyRender(AdtErDashboard, {}, 206)}
                    />
                    <Route
                        path='/dashboards/ipastar'
                        element={LazyRender(IPAStarDashboard, {}, 198)}
                    />
                    <Route
                        path='/audit'
                        element={LazyRender(AuditHistory, {}, 36)}
                    />
                    <Route
                        path='/providersearch'
                        element={LazyRender(ProviderSearch, {}, 152)}
                    />
                    <Route
                        path='/census/dailycensus'
                        element={LazyRender(DailyCensus, {}, 37)}
                    />
                    <Route
                        path='/census/dailydischarge'
                        element={LazyRender(DailyDischarge, {}, 38)}
                    />
                    <Route
                        path='/census/admission'
                        element={LazyRender(DailyAdmission, {}, 117)}
                    />
                    {/*CareOnDemand Routes*/}
                    <Route
                        path='/careondemand/mypanel'
                        element={LazyRender(MyCoDPanel, {}, 62)}
                    />
                    <Route
                        path='/careondemand/mypanel/:memberId/:pbpEffectiveDate'
                        element={LazyRender(MyCoDPanel, {}, 62)}
                    />
                    <Route
                        path='/careondemand/audithistory'
                        element={LazyRender(
                            CareOnDemandAuditHistoryTab,
                            {
                                telehealthModuleType: TELEHEALTH_MODULES.CAREONDEMAND,
                            },
                            64,
                        )}
                    />
                    <Route
                        path='/careondemand/admin'
                        element={LazyRender(
                            CODAppointmentAdmin,
                            {
                                telehealthModuleType: TELEHEALTH_MODULES.CAREONDEMAND,
                            },
                            102,
                        )}
                    />
                    <Route
                        path='/careondemand/livedashboard'
                        element={LazyRender(
                            LiveDashboardTab,
                            {
                                module: modules.CareOnDemand,
                            },
                            56,
                        )}
                    />
                    <Route
                        path='/careondemand/providermanager'
                        element={LazyRender(ProviderManagerPanel, {}, 192)}
                    />
                    {/*AvaExpress Routes*/}
                    <Route
                        path='/eligibility/member?/:memberId?'
                        element={LazyRender(EligibilityAndBenefits, {}, 28)}
                    />
                    <Route
                        path='/eligibility/userguide'
                        element={LazyRender(
                            EligibilityuideLines,
                            {
                                module: modules.EligibilityAndBenefits,
                                showIndex: false,
                            },
                            28,
                        )}
                    />
                    <Route
                        path='/ebspecialist'
                        element={LazyRender(EligibilityAndBenefitsSpecialist, {}, 31)}
                    />
                    <Route
                        path='/ebspecialist/userguide'
                        element={LazyRender(
                            EligibilityuideLines,
                            {
                                module: modules.EligibilityAndBenefits,
                                showIndex: false,
                            },
                            31,
                        )}
                    />
                    <Route
                        path='/authorizations'
                        element={LazyRender(Authorization, {}, 30)}
                    />
                    <Route
                        path='/authorizations/userguide'
                        element={LazyRender(
                            AuthorizationGuideLines,
                            {
                                module: modules.Authorizations,
                                showIndex: false,
                            },
                            30,
                        )}
                    />
                    <Route
                        path='/claims'
                        element={LazyRender(Claims, {}, 29)}
                    />
                    <Route
                        path='/claims/userguide'
                        element={LazyRender(ClaimsGuideLines, { module: modules.ClaimsInquiry, showIndex: false }, 29)}
                    />
                    <Route
                        path='/agents/eligibility'
                        element={LazyRender(EligibilityAndBenefitsAgent, {}, 65)}
                    />
                    <Route
                        path='/ava/dailycensus'
                        element={LazyRender(AVADailyCensus, {}, 183)}
                    />
                    <Route
                        path='/ava/dailydischarge'
                        element={LazyRender(AVADailyDischarge, {}, 184)}
                    />
                    <Route
                        path='/ava/admission'
                        element={LazyRender(AVADailyAdmit, {}, 185)}
                    />
                    <Route
                        path='/um/authorizations'
                        element={LazyRender(UMAuthorizationWrapper, {}, 66)}
                    />
                    <Route
                        path='/um/authorizations/:memberId'
                        element={LazyRender(UMAuthorizationWrapper, {}, 66)}
                    />
                    <Route
                        path='/um/faxes'
                        element={LazyRender(UMFaxManagement, {}, 141)}
                    />
                    <Route
                        path='/um/admin'
                        element={LazyRender(UMAdmin, {}, 154)}
                    />
                    <Route
                        path='/um/adtadmissions'
                        element={LazyRender(ADTAdmissionsWrapper, {}, 66)}
                    />
                    <Route
                        path='/um/productivity'
                        element={LazyRender(UMProductivityParent, {}, 143)}
                    />
                    <Route
                        path='/um/auth-metrics'
                        element={LazyRender(AuthMetrics, {}, 169)}
                    />
                    <Route
                        path='/agents/claims'
                        element={LazyRender(ClaimsInternal, {}, 67)}
                    />
                    {/*AvaScheduler Routes*/}
                    <Route
                        path='/avascheduler/calendar'
                        element={LazyRender(ProviderCalendar, {}, 103)}
                    />
                    <Route
                        path='/provider-crm/myqueue'
                        element={LazyRender(ProviderCRMMyQueue, {}, 187)}
                    />
                    <Route
                        path='/provider-appeals-grievances/ag360'
                        element={LazyRender(ProviderAppealsAndGrievances, {}, 189)}
                    />
                    {/*CRM Routes*/}
                    <Route
                        path='/crm/myqueue'
                        element={LazyRender(MyQueue, {}, 49)}
                    />
                    <Route
                        path='/crm/myqueue/:crmItem/:crmItemId'
                        element={LazyRender(MyQueue, {}, 49)}
                    />
                    <Route
                        path='/crm/productivity'
                        element={LazyRender(AgentsProductivity, {}, 91)}
                    />
                    <Route
                        path='/crm/advancedsearch'
                        element={LazyRender(AdvancedSearch, {}, 50)}
                    />
                    <Route
                        path='/crm/dashboards'
                        element={LazyRender(DisenrollmentDashboards, {}, 42)}
                    />
                    <Route
                        path='/crm/campaign-config'
                        element={LazyRender(CampaignConfigurator, {}, 69)}
                    />
                    <Route
                        path='/crm/benefit-config'
                        element={LazyRender(BenefitConfigurator, {}, 69)}
                    />
                    <Route
                        path='/crm/login-accounts/search'
                        element={LazyRender(CrmLoginAccounts, {}, 83)}
                    />
                    <Route
                        path='/crm/member-escalation'
                        element={LazyRender(MemberEscalation, {}, 181)}
                    />
                    <Route
                        path='/crm/queue-reports'
                        element={LazyRender(QueueReports, {}, 190)}
                    />
                    <Route
                        path='/crm-admin/text-replace'
                        element={LazyRender(TextReplace, {}, 69)}
                    />
                    <Route
                        path='/crm-admin/case-autoassign'
                        element={LazyRender(CaseReassign, {}, 156)}
                    />
                    <Route
                        path='/crm-admin/form-builder'
                        element={LazyRender(FormBuilder, {}, 116)}
                    />
                    <Route
                        path='/crm-admin/campaigns'
                        element={LazyRender(CampaignHome, {}, 116)}
                    />
                    <Route
                        path='/crm-admin/service-actions'
                        element={LazyRender(CrmServiceActions, {}, 175)}
                    />
                    <Route
                        path='/crm-admin/queues'
                        element={LazyRender(CrmQueues, {}, 116)}
                    />
                    <Route
                        path='/crm-admin/request-plan-materials'
                        element={LazyRender(CrmRequestPlanMaterials, {}, 116)}
                    />
                    <Route
                        path='/crm-admin/sticky-notes'
                        element={LazyRender(CrmStickyNotes, {}, 116)}
                    />
                    <Route
                        path='/crm-admin/communications-manager'
                        element={LazyRender(CommunicationsManager, {}, 120)}
                    />
                    <Route
                        path='/crm-admin/benefits'
                        element={LazyRender(CrmAdminBenefits, {}, 116)}
                    />
                    <Route
                        path='/crm/member-portal-audit-history'
                        element={LazyRender(CRMMemberPortalAdminAuditHistoryPage, {}, 157)}
                    />
                    <Route
                        path='/crm/campaign-stats'
                        element={LazyRender(CampaignStats, {}, 178)}
                    />
                    <Route
                        path='/crm/member-portal-admin-console'
                        element={LazyRender(MemberPortalAdminConsole, {}, 179)}
                    />
                    <Route
                        path='/crm/member-portal-banner-manager'
                        element={LazyRender(MemberPortalBannerManager, {}, 209)}
                    />
                    <Route
                        path='/crm/member-portal-banner-manager/new'
                        element={LazyRender(UpsertMemberPortalBannerManager, {}, 209)}
                    />
                    <Route
                        path='/crm/member-portal-banner-manager/edit/:id'
                        element={LazyRender(UpsertMemberPortalBannerManager, {}, 209)}
                    />
                    {/*Encounters Routes*/}
                    <Route
                        path='/encounters/search'
                        element={LazyRender(Encounters, {}, 110)}
                    />
                    {/*ClaimsAuditor Routes*/}
                    <Route
                        path='/claimsauditor/claims'
                        element={LazyRender(PendingClaims, {}, 45)}
                    />
                    <Route
                        path='/claimsauditor/admin'
                        element={LazyRender(ClaimsAuditorAdmin, {}, 60)}
                    />
                    <Route
                        path='/claimsauditor/roles'
                        element={LazyRender(ClaimsAuditorRoles, {}, 88)}
                    />
                    <Route
                        path='/claimsauditor/advancedaudits'
                        element={LazyRender(AdvancedAudits, {}, 98)}
                    />
                    {/*AvaCharts Routes*/}
                    <Route
                        path='/charts/search'
                        element={LazyRender(ChartsSearch, {}, 46)}
                    />
                    <Route
                        path='/charts/upload'
                        element={LazyRender(ChartsUpload, {}, 46)}
                    />
                    <Route
                        path='/charts/splitvalidation'
                        element={LazyRender(ChartsSplitAndValidation, {}, 46)}
                    />
                    <Route
                        path='/charts/coderstars'
                        element={LazyRender(ChartsStarsCoderView, {}, 46)}
                    />
                    <Route
                        path='/charts/codermra'
                        element={LazyRender(ChartsMraCoderView, {}, 46)}
                    />
                    <Route
                        path='/charts/starsadmin'
                        element={LazyRender(ChartsStarsAdmin, {}, 46)}
                    />
                    <Route
                        path='/charts/dashboard'
                        element={LazyRender(ChartsDashboard, {}, 46)}
                    />
                    {/*UserManagement Routes*/}
                    <Route
                        path='/usermanagement/users'
                        element={LazyRender(UserManagementUsers, {}, 18)}
                    />
                    <Route
                        path='/usermanagement/providers'
                        element={LazyRender(UserManagementProviders, {}, 20)}
                    />
                    <Route
                        path='/usermanagement/userevents'
                        element={LazyRender(UserManagementUserEvents, {}, 74)}
                    />
                    <Route
                        path='/usermanagement/groups'
                        element={LazyRender(UserManagementGroupTab, {}, 19)}
                    />
                    <Route
                        path='/usermanagement/organizations'
                        element={LazyRender(UserManagementOrganizations, {}, 23)}
                    />
                    <Route
                        path='/usermanagement/domains'
                        element={LazyRender(UserManagementDomains, {}, 200)}
                    />
                    <Route
                        path='/usermanagement/userreport'
                        element={LazyRender(UserManagementUserReport, {}, 44)}
                    />
                    {/*LocalUserManagement Routes*/}
                    <Route
                        path='/localusermanagement/users'
                        element={LazyRender(LocalUserManagementUserTab, {}, 197)}
                    />
                    <Route
                        path='/localusermanagement/faqs'
                        element={LazyRender(LocalUserManagementFaqs, {}, 197)}
                    />
                    {/*PatientPriority Routes */}
                    <Route
                        path='/patientpriority/worklist/'
                        element={LazyRender(PatientPriorityWorkList, {}, 13)}
                    />
                    <Route
                        path='/patientpriority/patients/'
                        element={LazyRender(PatientPriorityAllPatients, {}, 14)}
                    />
                    <Route
                        path='/patientpriority/dashboards/'
                        element={LazyRender(PatientPriorityDashboards, {}, 15)}
                    />
                    <Route
                        path='/patientpriority/guidelines/'
                        element={LazyRender(
                            PatientPriorityGuidelines,
                            {
                                module: modules.PatientPriority,
                                showIndex: true,
                            },
                            16,
                        )}
                    />
                    <Route
                        path='/patientpriority/gapsreview/'
                        element={LazyRender(PatientPriorityGapsReview, {}, 43)}
                    />
                    <Route
                        path='/patientpriority/supplementalreview/'
                        element={LazyRender(PatientPrioritySupplementalReview)}
                    />
                    <Route
                        path='/patientpriority/audit/'
                        element={LazyRender(PatientPriorityAudit, {}, 166)}
                    />
                    {/*Coder360 Routes */}
                    <Route
                        path='/coder360/chartreview/'
                        element={LazyRender(Coder360, {}, 70)}
                    />
                    <Route
                        path='/coder360/submissions/'
                        element={LazyRender(Coder360SubmissionsModule, {}, 71)}
                    />
                    <Route
                        path='/coder360/allcrrs/'
                        element={LazyRender(Coder360MyCRRsAdminModule, { showAllCRRs: true }, 114)}
                    />
                    {/*MyOffice Routes*/}
                    <Route
                        path='/myoffice/dashboard'
                        element={LazyRender(Dashboard, {}, 72)}
                    />
                    <Route
                        path='/form-builder'
                        element={LazyRender(FormBuilder, {}, null)}
                    />
                    {/*Ezm Routes*/}
                    <Route
                        path='/ezm/network/service-organization-admin/'
                        element={LazyRender(EzmServiceOrganizationAdmin, {}, 92)}
                    />
                    <Route
                        path='/ezm/network/provider-group-admin/'
                        element={LazyRender(EzmProviderGroupAdmin, {}, null)}
                    />
                    <Route
                        path='/ezm/member/admin/'
                        element={LazyRender(EzmMemberAdminPage, {}, 76)}
                    />
                    <Route
                        path='/ezm/cms/batch-status-report/'
                        element={LazyRender(EzmBatchJobStatusReport, {}, null)}
                    />
                    <Route
                        path='/ezm/cms/batch-error-report/'
                        element={LazyRender(EzmBatchJobErrorReport, {}, null)}
                    />
                    <Route
                        path='/ezm/provider-portal-admin/account-search/'
                        element={LazyRender(EzmProviderPortalAdminReport, {}, 85)}
                    />
                    <Route
                        path='/ezm/provider-portal-admin/registration-manager'
                        element={LazyRender(ProviderToolsAccountEditor, {}, 85)}
                    />
                    <Route
                        path='/ezm/utilities/token-viewer/'
                        element={LazyRender(EzmTokenViewer, {}, null)}
                    />
                    <Route
                        path='/ezm/health-plan-management/search/'
                        element={LazyRender(EzmHealthPlanManagement, {}, 86)}
                    />
                    <Route
                        path='/ezm/health-plan-management-admin/benefit-editor/'
                        element={LazyRender(EzmHealthPlanManagementBenefit, {}, 87)}
                    />
                    <Route
                        path='/ezm/invalid-member-portal/'
                        element={LazyRender(InvalidMemberEnrollment, {}, 153)}
                    />
                    <Route
                        path='/ezm/eob-notifications-history/'
                        element={LazyRender(EobNotificationsHistoryViewer, {}, 163)}
                    />
                    {/* Provider Tools Routes */}
                    <Route
                        path='/provider-tools-admin'
                        element={LazyRender(ProviderToolsAdmin, {}, 164)}
                    />
                    <Route
                        path='/provider-tools/eligibility-check/'
                        element={LazyRender(ProviderToolsEligibility, {}, null)}
                    />
                    <Route
                        path='/provider-tools/report-upload/'
                        element={LazyRender(ProviderToolsReportUpload, {}, null)}
                    />
                    <Route
                        path='/provider-tools/report-upload/userguide'
                        element={LazyRender(
                            ReportsSubmissionGuideLines,
                            {
                                module: modules.ReportsSubmissionort,
                                showIndex: false,
                            },
                            90,
                        )}
                    />
                    <Route
                        path='/ezm/network/contract-info'
                        element={LazyRender(ContractInfo, {}, 94)}
                    />
                    <Route
                        path='/ezm/network/hospital-maintenance'
                        element={LazyRender(HospitalMaintenance, {}, 108)}
                    />
                    <Route
                        path='/ezm/network/template-manager'
                        element={LazyRender(TemplateManager, {}, 138)}
                    />
                    <Route
                        path='/ezm/network/data-import-utility'
                        element={LazyRender(DataImportUtility, {}, null)}
                    />
                    <Route
                        path='/ezm/medical-group/ipa-administration'
                        element={LazyRender(IpaAdministrationPage, {}, 99)}
                    />
                    <Route
                        path='/ezm/provider-data-update-request'
                        element={LazyRender(ProviderDataUpdateRequest, {}, 158)}
                    />
                    <Route
                        path='/ezm/network/data-utility'
                        element={LazyRender(ProviderDataUtility, {}, 193)}
                    />
                    {/* Admin routes */}
                    <Route
                        path='/admin/applications'
                        element={LazyRender(AdminApplications, {}, 106)}
                    />
                    <Route
                        path='/ezm/appeals-grievance'
                        element={LazyRender(AG360, {}, 104)}
                    />
                    <Route
                        path='/content-library'
                        element={LazyRender(ContentLibrary, {}, 106)}
                    />
                    <Route
                        path='/content-manager'
                        element={LazyRender(ContentManager, {}, 106)}
                    />
                    <Route
                        path='/performance-view'
                        element={LazyRender(PerformanceModule, {}, 140)}
                    />
                    <Route
                        path='/admin/static-file-manager'
                        element={LazyRender(StaticFileManager, { id: 'static-file-manager-admin' }, 119)}
                    />
                    {/* Broker Portal routes */}
                    <Route
                        path='/broker/reports/*'
                        element={LazyRender(BrokerReports, { id: 'reports' }, 127)}
                    />
                    <Route
                        path='/broker/file-manager'
                        element={LazyRender(FileManagerBroker, { id: 'static-file-manager-broker' }, null)}
                    />
                    <Route
                        path='/broker/analytics'
                        element={LazyRender(
                            BrokerAnalytics,
                            {
                                id: 'broker-analytics',
                            },
                            129,
                        )}
                    />
                    <Route
                        path='/broker/accounts/*'
                        element={LazyRender(
                            BrokerAccountManager,
                            {
                                id: 'broker-accounts',
                            },
                            159,
                        )}
                    />
                    <Route
                        path='/broker/crm'
                        element={LazyRender(
                            BrokerCasesManager,
                            {
                                id: 'broker-crm',
                            },
                            191,
                        )}
                    />
                    <Route
                        path='/broker/commissions'
                        element={LazyRender(
                            BrokerCommissions,
                            {
                                id: 'broker-commissions',
                            },
                            165,
                        )}
                    />
                    <Route
                        path='/broker/admin'
                        element={LazyRender(
                            BrokerUserManager,
                            {
                                id: 'broker-admin',
                            },
                            84,
                        )}
                    />
                    <Route
                        path='/broker/user-guides'
                        element={LazyRender(
                            BrokerUserGuides,
                            {
                                id: 'broker-user-guides',
                            },
                            173,
                        )}
                    />
                    <Route
                        path='/broker/brokers'
                        element={LazyRender(BrokerSearch, {}, null)}
                    />
                    <Route
                        path='/broker/sales'
                        element={LazyRender(SalesRepSearch, {}, null)}
                    />
                    <Route
                        path='/broker/portal-admin'
                        element={LazyRender(BrokerPortalAdmin, {}, 208)}
                    />
                    {/*CareManagement Routes*/}
                    <Route
                        path='/caremanagement/snp-team-member'
                        element={LazyRender(SNPTeamMeber, {}, 145)}
                    />
                    <Route
                        path='/caremanagement/post-discharge-program-manager'
                        element={LazyRender(PostDischargeProgramManager, {}, 146)}
                    />
                    <Route
                        path='/caremanagement/outpatient-team-member'
                        element={LazyRender(OutpatientTeam, {}, 148)}
                    />
                    <Route
                        path='/caremanagement/outpatient-team-member/:cmItem/:cmItemId'
                        element={LazyRender(OutpatientTeam, {}, 148)}
                    />
                    <Route
                        path='/caremanagement/dashboards'
                        element={LazyRender(CMDashboards, {}, 113)}
                    />
                    <Route
                        path='/caremanagement/paper-hra'
                        element={LazyRender(CMPaperHRA, {}, 161)}
                    />
                    <Route
                        path='/ezm/sales-rep'
                        element={LazyRender(SalesRepAdminPage, {}, 151)}
                    />
                    <Route
                        path='/ezm/certification-session-manager'
                        element={LazyRender(CertificationSessionManager, {}, 137)}
                    />
                    <Route
                        path='/ezm/notification-manager'
                        element={LazyRender(NotificationManagerPage, {}, 149)}
                    />
                    <Route
                        path='/ezm/approval-workflow'
                        element={LazyRender(ApprovalWorkflowPage, {}, 186)}
                    />
                    <Route
                        path='/compliancereport/search'
                        element={LazyRender(ComplianceReportSearch, {}, 136)}
                    />
                    <Route
                        path='/webenrollment/search'
                        element={LazyRender(WebEnrollmentsSearch, {}, null)}
                    />
                    <Route
                        path='/webenrollment/review'
                        element={LazyRender(ReviewWebEnrollment, {}, null)}
                    />
                    <Route
                        path='/webenrollment/cmseligibilitycheck'
                        element={LazyRender(CMSEligibilityCheck, {}, null)}
                    />
                    <Route
                        path='/memberEnrollment'
                        element={LazyRender(MemberEnrollment, {}, 139)}
                    />
                    <Route
                        path='/memberEnrollmentUpload'
                        element={LazyRender(MemberEnrollmentUploadAdmin, {}, 150)}
                    />
                    <Route
                        path='/ezm/enrollment-submission'
                        element={LazyRender(MemberEnrollmentSubmission, {}, 195)}
                    />
                    <Route
                        path='/memberEnrollment/ReportsAndUtilities'
                        element={LazyRender(MemberEnrollmentReportAndUtilitiesAdmin, {}, 172)}
                    />
                    <Route
                        path='/ezm/member-pcp-assignment-tasks'
                        element={LazyRender(MemberPCPAssignmentTasks, {}, 177)}
                    />
                    <Route
                        path='/memberEnrollment/AdminAndSupport'
                        element={LazyRender(AdminAndSupportAdminPortal, {}, 176)}
                    />
                    <Route
                        path='/MemberEnrollment/Configurations'
                        element={LazyRender(MemberEnrollmentConfigurations, {}, 202)}
                    />
                    <Route
                        path='/return-mail-escalations'
                        element={LazyRender(ReturnMailEscalations, {}, 182)}
                    />
                    <Route
                        path='/padr'
                        element={LazyRender(PADR, {}, 199)}
                    />
                    <Route
                        path='/MemberEnrollment/PcpRetroAssignment'
                        element={LazyRender(RetroPcpAssignment, {}, 204)}
                    />
                    <Route
                        path='/ezm/um-tools/users-importer'
                        element={LazyRender(AvaUsersImporter, {}, 205)}
                    />
                </Routes>
            </Layout>
        </LocalizationProvider>
    );
};

export default App;
